import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import { io } from 'socket.io-client'
import {
  getChatSessions,
  updateAgentStatus
} from '../services/(adminapi)/messagesAPI'

const QUEUED_TITLE = 'Chat waiting in queue'
const QUEUED_BODY = 'A visitor needs help. Open Chat Logs to join.'

const DashboardSocketContext = createContext(null)

/**
 * One shared agent / dashboard Socket.IO connection for authenticated users.
 * Also stores handoff assignment notifications for the top bar.
 */
export function DashboardSocketProvider({ children }) {
  const { isAuthenticated, user } = useSelector((s) => s.auth)
  const [socket, setSocket] = useState(null)
  const [sessionNotifications, setSessionNotifications] = useState([])
  const dismissedQueuedRef = useRef(new Set())

  const syncQueuedNotifications = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'Agent') return
    const myId = String(user?.id || '')
    try {
      const res = await getChatSessions()
      if (!res?.success || !Array.isArray(res.data)) return

      const pending = res.data.filter((s) => {
        const assignedTo = String(s.assigned_to?._id ?? s.assigned_to ?? '')
        if (s.status === 'queued') {
          return assignedTo === myId
        }
        if (s.status === 'assigned' && assignedTo === myId && !s.agent_joined_at) {
          return true
        }
        return false
      })

      setSessionNotifications((prev) => {
        const other = prev.filter((n) => n.kind !== 'queued' && n.kind !== 'pending_join')
        const prevPendingByConv = new Map(
          prev
            .filter((n) => n.kind === 'queued' || n.kind === 'pending_join')
            .map((n) => [n.conversationId, n])
        )

        const pendingNotifs = pending
          .map((s) => {
            const convId = String(
              s.conversation_id?._id ?? s.conversation_id
            )
            if (dismissedQueuedRef.current.has(convId)) return null
            const existing = prevPendingByConv.get(convId)
            const isAssigned = s.status === 'assigned'
            return {
              id: existing?.id ?? `${isAssigned ? 'assigned' : 'queued'}-${convId}`,
              kind: isAssigned ? 'pending_join' : 'queued',
              title: isAssigned ? 'Chat assigned to you' : QUEUED_TITLE,
              body: isAssigned
                ? 'Open Chat Logs and join to start responding.'
                : QUEUED_BODY,
              read: existing?.read ?? false,
              createdAt:
                existing?.createdAt ??
                new Date(s.updatedAt || s.createdAt || Date.now()).getTime(),
              chatSessionId: String(s._id),
              conversationId: convId
            }
          })
          .filter(Boolean)

        return [...pendingNotifs, ...other].slice(0, 50)
      })
    } catch {
      // ignore
    }
  }, [isAuthenticated, user?.role, user?.id])

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'Agent') return undefined
    updateAgentStatus('online').catch(() => {})
    syncQueuedNotifications()
    return undefined
  }, [isAuthenticated, user?.role, syncQueuedNotifications])

  useEffect(() => {
    if (!isAuthenticated) {
      setSocket((prev) => {
        if (prev) prev.disconnect()
        return null
      })
      setSessionNotifications([])
      dismissedQueuedRef.current.clear()
      return undefined
    }

    const token = localStorage.getItem('token')
    if (!token) return undefined

    const apiUrl = process.env.REACT_APP_API_URL || ''
    const base = apiUrl.startsWith('http')
      ? apiUrl.replace(/\/api\/?$/, '')
      : window.location.origin

    const s = io(base, { path: '/agent-ws', auth: { token } })
    setSocket(s)

    const pushSessionNotification = (payload, title, body) => {
      const convId = payload?.conversationId
        ? String(payload.conversationId)
        : null
      setSessionNotifications((prev) => {
        if (
          convId &&
          prev.some((n) => n.conversationId === convId && !n.read)
        ) {
          return prev
        }
        const id =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
        return [
          {
            id,
            kind: 'assigned',
            title,
            body,
            read: false,
            createdAt: Date.now(),
            chatSessionId: payload?.chatSessionId,
            conversationId: convId
          },
          ...prev
        ].slice(0, 50)
      })
    }

    const myAgentId = String(user?.id || '')

    const onNewChatQueued = (payload) => {
      if (user?.role !== 'Agent') return
      const target = payload?.agentUserId
      if (target && String(target) !== myAgentId) return
      syncQueuedNotifications()
    }

    const onQueueUpdate = () => {
      if (user?.role !== 'Agent') return
      syncQueuedNotifications()
    }

    const onNewChatAssigned = (payload) => {
      if (user?.role !== 'Agent') return
      pushSessionNotification(
        payload,
        'New chat assigned',
        'A chat was assigned to you. Open Chat Logs to respond.'
      )
    }

    s.on('new_chat_queued', onNewChatQueued)
    s.on('queue_update', onQueueUpdate)
    s.on('new_chat_assigned', onNewChatAssigned)
    s.on('next_chat_ready', onNewChatAssigned)

    return () => {
      s.off('new_chat_queued', onNewChatQueued)
      s.off('queue_update', onQueueUpdate)
      s.off('new_chat_assigned', onNewChatAssigned)
      s.off('next_chat_ready', onNewChatAssigned)
      s.disconnect()
      setSocket(null)
    }
  }, [isAuthenticated, user?.role, syncQueuedNotifications])

  const markNotificationRead = useCallback((id) => {
    setSessionNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    )
  }, [])

  const markAllNotificationsRead = useCallback(() => {
    setSessionNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }, [])

  const dismissNotification = useCallback((id) => {
    setSessionNotifications((prev) => {
      const target = prev.find((n) => n.id === id)
      if (
        (target?.kind === 'queued' || target?.kind === 'pending_join') &&
        target.conversationId
      ) {
        dismissedQueuedRef.current.add(target.conversationId)
      }
      return prev.filter((n) => n.id !== id)
    })
  }, [])

  const unreadCount = useMemo(
    () => sessionNotifications.filter((n) => !n.read).length,
    [sessionNotifications]
  )

  const value = useMemo(
    () => ({
      socket,
      sessionNotifications,
      unreadCount,
      markNotificationRead,
      markAllNotificationsRead,
      dismissNotification
    }),
    [
      socket,
      sessionNotifications,
      unreadCount,
      markNotificationRead,
      markAllNotificationsRead,
      dismissNotification
    ]
  )

  return (
    <DashboardSocketContext.Provider value={value}>
      {children}
    </DashboardSocketContext.Provider>
  )
}

export function useDashboardSocket() {
  const ctx = useContext(DashboardSocketContext)
  if (!ctx) {
    throw new Error('useDashboardSocket must be used within DashboardSocketProvider')
  }
  return ctx
}
